import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faUndoAlt } from "@fortawesome/pro-solid-svg-icons/faUndoAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import { Button } from "@mapmycustomers/ui";

import lassoToolIcon from "@app/assets/map/lassoMode.svg";
import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import AddToGroupTools from "@app/scene/map/components/tools/LassoToolPanel/AddToGroupTools";
import LassoBulkEdit from "@app/scene/map/components/tools/LassoToolPanel/LassoBulkEdit";
import StickyBar from "@app/scene/map/components/tools/StickyBar";
import LassoToolMode from "@app/scene/map/enums/LassoToolMode";
import {
  cleanLassoPaths,
  clearRecordsList,
  exitLassoMode,
  restartSelection,
} from "@app/scene/map/store/actions";
import {
  getLassoMode,
  getMapLassoAllowAdd,
  getMapLassoPathsCount,
  getSelectedRecordsCount,
  isLassoLoading,
} from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

import styles from "./LassoToolPanel.module.scss";

interface Props {
  allowAdd: boolean;
  cleanLassoPaths: typeof cleanLassoPaths;
  clearRecordsList: typeof clearRecordsList;
  exitLassoMode: typeof exitLassoMode;
  loading: boolean;
  mode: LassoToolMode;
  onExit: () => void;
  pathCount: number;
  recordsCount: number;
  restartSelection: typeof restartSelection;
}

const messages = defineMessages({
  count: {
    id: "map.tool.lasso.toolbar.count",
    defaultMessage: "{count, plural, =0 {No pins} one {{count} pin} other {{count} pins}} selected",
    description: "Lasso pins counter",
  },
  restart: {
    id: "map.tool.lasso.toolbar.restart",
    defaultMessage: "Restart",
    description: "Lasso paths restart button title",
  },
  text: {
    id: "map.tool.lasso.toolbar.text",
    defaultMessage: "Draw a circle on the map to select pins with lasso",
    description: "Lasso Mode description",
  },
  title: {
    id: "map.tool.lasso.toolbar.exitButton",
    defaultMessage: "Exit Lasso Mode",
    description: "Exit Pin Drop Mode button title",
  },
});

const LassoToolPanel: React.FC<Props> = ({
  cleanLassoPaths,
  clearRecordsList,
  exitLassoMode,
  loading,
  mode,
  onExit,
  pathCount,
  recordsCount,
  restartSelection,
}) => {
  const analytics = useExtendedAnalytics("Lasso Tool", useAnalytics());
  const intl = useIntl();

  const handleExit = useCallback(() => {
    exitLassoMode();
    onExit?.();
    analytics.cancelled(["Exit lasso tool"]);
  }, [analytics, exitLassoMode, onExit]);

  const handleRestart = useCallback(() => {
    cleanLassoPaths();
    clearRecordsList();
    restartSelection();
    analytics.clicked(["Restart"]);
  }, [analytics, cleanLassoPaths, clearRecordsList, restartSelection]);

  const hasPaths = pathCount > 0;
  const hasSelectedRecords = recordsCount > 0;
  const hasAnySelection = hasPaths || hasSelectedRecords;

  return (
    <Interactive>
      <StickyBar className={styles.container}>
        {hasAnySelection ? (
          <div className={styles.content}>
            {intl.formatMessage(messages.count, { count: recordsCount })}

            <div className={styles.restart}>
              <Tooltip placement="bottom" title={intl.formatMessage(messages.restart)}>
                <div>
                  <Button
                    className={styles.clearBtn}
                    disabled={loading}
                    icon={<FontAwesomeIcon icon={faUndoAlt} />}
                    iconOnly
                    onClick={handleRestart}
                  />
                </div>
              </Tooltip>
            </div>
          </div>
        ) : (
          <div className={styles.description}>
            {intl.formatMessage(messages.text)}
            <img alt="Lasso tool" src={lassoToolIcon} />
          </div>
        )}

        <div className={styles.action}>
          <Button onClick={handleExit}>{intl.formatMessage(messages.title)}</Button>
        </div>
      </StickyBar>

      {hasSelectedRecords && (
        <>
          {mode === LassoToolMode.ADD_GROUP_PINS ? (
            <div className={styles.tools}>
              <AddToGroupTools onExit={onExit} />
            </div>
          ) : (
            <LassoBulkEdit />
          )}
        </>
      )}
    </Interactive>
  );
};

const mapStateToProps = (state: RootState) => ({
  allowAdd: getMapLassoAllowAdd(state),
  loading: isLassoLoading(state),
  mode: getLassoMode(state),
  pathCount: getMapLassoPathsCount(state),
  recordsCount: getSelectedRecordsCount(state),
});

const mapDispatchToProps = {
  cleanLassoPaths,
  clearRecordsList,
  exitLassoMode,
  restartSelection,
};

export default connect(mapStateToProps, mapDispatchToProps)(LassoToolPanel);
